import { DEFAULT_REGISTRANT_LICENSE_COLUMN_VISIBILITY } from "@/components/registrant/dashboard/license-list/license-table";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import { useEffect, useState } from "react";

/** Company answers used to have their own `cq:<question id>` columns; they are
 * question columns now, so a stored pick would filter or hide a column that no
 * longer exists. */
function withoutRetiredCompanyColumns<T>(
  stored: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(stored).filter(([key]) => !key.startsWith("cq:")),
  );
}

export function useRegistrantDashboardFilters() {
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>(
    () =>
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_JURISDICTIONS,
        [],
      ),
  );

  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () =>
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_LICENSE_TYPES,
        [],
      ),
  );

  const [isMapView, setIsMapView] = useState(() =>
    sessionStorage.get(SESSION_KEYS.REGISTRANT_DASHBOARD_IS_MAP_VIEW, false),
  );

  const [selectedTab, setSelectedTab] = useState(() =>
    sessionStorage.get(SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_TAB, "all"),
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    const stored = sessionStorage.get(
      SESSION_KEYS.REGISTRANT_DASHBOARD_LICENSE_COLUMN_VISIBILITY,
      {},
    );
    return {
      ...DEFAULT_REGISTRANT_LICENSE_COLUMN_VISIBILITY,
      ...withoutRetiredCompanyColumns<boolean>(stored),
    };
  });

  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    () =>
      withoutRetiredCompanyColumns<string[]>(
        sessionStorage.get(
          SESSION_KEYS.REGISTRANT_DASHBOARD_LICENSE_COLUMN_FILTERS,
          {},
        ),
      ),
  );

  const [bulkColumnFilters, setBulkColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    withoutRetiredCompanyColumns<string[]>(
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_COLUMN_FILTERS,
        {},
      ),
    ),
  );

  const [bulkColumnVisibility, setBulkColumnVisibility] = useState<
    Record<string, boolean>
  >(() =>
    withoutRetiredCompanyColumns<boolean>(
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_COLUMN_VISIBILITY,
        {},
      ),
    ),
  );

  const [selectedRegistrantAccountId, setSelectedRegistrantAccountId] =
    useState<string | null>(() =>
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_REGISTRANT_ACCOUNT_ID,
        null,
      ),
    );

  const [balanceDueCohortFilterActive, setBalanceDueCohortFilterActive] =
    useState(() =>
      sessionStorage.get(
        SESSION_KEYS.REGISTRANT_DASHBOARD_BALANCE_DUE_COHORT_ACTIVE,
        false,
      ),
    );

  const [
    bulkPaymentPayLaterCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
  ] = useState(() =>
    sessionStorage.get(
      SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_PAY_LATER_COHORT_ACTIVE,
      false,
    ),
  );

  const [selectedAms, setSelectedAms] = useState<string | null>(() =>
    sessionStorage.get(SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_AMS, null),
  );

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_JURISDICTIONS,
      selectedJurisdictions,
    );
  }, [selectedJurisdictions]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_LICENSE_TYPES,
      selectedLicenseTypes,
    );
  }, [selectedLicenseTypes]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_IS_MAP_VIEW,
      isMapView,
    );
  }, [isMapView]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_TAB,
      selectedTab,
    );
  }, [selectedTab]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_LICENSE_COLUMN_VISIBILITY,
      columnVisibility,
    );
  }, [columnVisibility]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_LICENSE_COLUMN_FILTERS,
      columnFilters,
    );
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_COLUMN_FILTERS,
      bulkColumnFilters,
    );
  }, [bulkColumnFilters]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_COLUMN_VISIBILITY,
      bulkColumnVisibility,
    );
  }, [bulkColumnVisibility]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_REGISTRANT_ACCOUNT_ID,
      selectedRegistrantAccountId,
    );
  }, [selectedRegistrantAccountId]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_BALANCE_DUE_COHORT_ACTIVE,
      balanceDueCohortFilterActive,
    );
  }, [balanceDueCohortFilterActive]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_PAY_LATER_COHORT_ACTIVE,
      bulkPaymentPayLaterCohortFilterActive,
    );
  }, [bulkPaymentPayLaterCohortFilterActive]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_AMS,
      selectedAms,
    );
  }, [selectedAms]);

  return {
    selectedJurisdictions,
    setSelectedJurisdictions,
    selectedLicenseTypes,
    setSelectedLicenseTypes,
    isMapView,
    setIsMapView,
    selectedTab,
    setSelectedTab,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
    bulkColumnFilters,
    setBulkColumnFilters,
    bulkColumnVisibility,
    setBulkColumnVisibility,
    selectedRegistrantAccountId,
    setSelectedRegistrantAccountId,
    balanceDueCohortFilterActive,
    setBalanceDueCohortFilterActive,
    bulkPaymentPayLaterCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    selectedAms,
    setSelectedAms,
  };
}
